'use strict';
(() => {
  if (window.IdolnestMotion) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const cards = new WeakSet();
  const images = new WeakSet();
  const imageCleanups = new Map();
  const artistStates = new Map();
  const running = new Set();
  const cardSelector = '[data-in-card-layout], [data-in-catalog-item]';
  const ease = 'cubic-bezier(.22,1,.36,1)';
  const visible = node => node.isConnected && !node.hidden && node.getClientRects().length > 0;
  const find = (scope, selector) => [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];

  function animate(node, frames, options) {
    if (reduced.matches || !node.animate || !visible(node)) return;
    const animation = node.animate(frames, {duration:440, easing:ease, ...options});
    running.add(animation);
    animation.finished.catch(() => {}).finally(() => running.delete(animation));
    return animation;
  }

  function reveal(nodes, direction = 0) {
    let index = 0;
    for (const node of nodes) {
      if (!visible(node)) continue;
      const rect = node.getBoundingClientRect();
      if (rect.right <= 0 || rect.left >= innerWidth || rect.bottom <= 0 || rect.top >= innerHeight) continue;
      animate(node, [
        {opacity:.15, transform:`translate3d(${direction * 22}px,14px,0) scale(.975)`},
        {opacity:1, transform:'translate3d(0,0,0) scale(1)'}
      ], {delay:Math.min(index++, 5) * 48});
    }
  }

  const cardObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    const entering = entries.filter(entry => entry.isIntersecting && visible(entry.target));
    for (const entry of entering) cardObserver.unobserve(entry.target);
    reveal(entering.map(entry => entry.target));
  }, {threshold:.08}) : null;

  const imageObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    for (const entry of entries) entry.target.classList.toggle('in-motion-image-visible', entry.isIntersecting);
  }, {rootMargin:'100px'}) : null;

  function watchImage(img) {
    if (images.has(img)) return;
    images.add(img);
    const frame = img.closest('.product-image-wrap, [data-in-media]');
    if (!frame || img.complete) return;
    frame.classList.add('in-motion-image-pending');
    imageObserver?.observe(frame);
    if (!imageObserver) frame.classList.add('in-motion-image-visible');
    const finish = event => {
      clearTimeout(timer);
      img.removeEventListener('load', finish);
      img.removeEventListener('error', finish);
      imageObserver?.unobserve(frame);
      frame.classList.remove('in-motion-image-pending','in-motion-image-visible');
      imageCleanups.delete(img);
      const primary = frame.matches('[data-in-media].is-active') || img.fetchPriority === 'high';
      if (event?.type === 'load' && !primary && img.getBoundingClientRect().top > innerHeight * .5) {
        animate(img, [{opacity:.6},{opacity:1}], {duration:200});
      }
    };
    const timer = setTimeout(finish, 12000);
    imageCleanups.set(img, () => finish());
    img.addEventListener('load', finish, {once:true});
    img.addEventListener('error', finish, {once:true});
    if (img.complete) finish();
  }

  function init(scope = document, initial = false) {
    for (const root of find(scope, '[data-in-shared-artists]')) {
      if (!artistStates.has(root)) artistStates.set(root, {
        category:root.querySelector('[data-in-artist-category-input]:checked')?.value,
        animations:[]
      });
    }
    for (const node of find(scope, cardSelector)) {
      if (cards.has(node)) continue;
      cards.add(node);
      const rect = node.getBoundingClientRect();
      if (initial && visible(node) && rect.top < innerHeight && rect.bottom > 0) {
        if (new URL(location.href).searchParams.has('page')) {
          animate(node,[{transform:'translate3d(0,10px,0)'},{transform:'translate3d(0,0,0)'}],{duration:320});
        }
        continue;
      }
      cardObserver?.observe(node);
    }
    for (const img of find(scope, '.product-image-wrap img, [data-in-media] img')) watchImage(img);
  }

  function switchArtists(input) {
    const root = input.closest('[data-in-shared-artists]');
    const track = root?.querySelector('.artist-track');
    const state = artistStates.get(root);
    if (!track || !state || state.category === input.value) return;
    const order = [...root.querySelectorAll('[data-in-artist-category-input]')].map(node => node.value);
    const direction = order.indexOf(input.value) >= order.indexOf(state.category) ? 1 : -1;
    for (const animation of state.animations) animation?.cancel();
    state.category = input.value;
    track.scrollLeft = 0;
    const incoming = [...track.querySelectorAll('.artist-card')].filter(node => node.dataset.inArtistCategory === input.value);
    state.animations = [];
    let index = 0;
    for (const node of incoming) {
      const rect = node.getBoundingClientRect();
      if (rect.left >= track.getBoundingClientRect().right || rect.right <= track.getBoundingClientRect().left) continue;
      const animation = animate(node, [
        {opacity:0,transform:`translate3d(${direction * 26}px,10px,0) scale(.96)`},
        {opacity:1,transform:'translate3d(0,0,0) scale(1)'}
      ], {duration:460,delay:Math.min(index++,5)*55,fill:'backwards'});
      if (animation) state.animations.push(animation);
    }
    const label = input.nextElementSibling;
    if (label) animate(label,[{transform:'scale(.96)'},{transform:'scale(1)'}],{duration:240});
  }

  document.addEventListener('change', event => {
    if (event.target.matches?.('[data-in-artist-category-input]')) switchArtists(event.target);
  });
  document.addEventListener('shopify:block:select', event => {
    const card = event.target.closest?.('[data-in-shared-artist]');
    const root = card?.closest('[data-in-shared-artists]');
    const state = artistStates.get(root);
    if (state) {
      state.animations.forEach(animation => animation.cancel());
      state.category = card.dataset.inArtistCategory;
    }
  });
  document.addEventListener('idolnest:catalog:rendered', event => {
    const grid = event.detail?.grid;
    if (!grid) return;
    init(grid);
    if (!event.detail.loading && event.detail.changed) {
      const nodes = [...grid.querySelectorAll(cardSelector)].filter(visible);
      for (const node of nodes) cardObserver?.unobserve(node);
      reveal(nodes);
    }
  });
  document.addEventListener('idolnest:cards:added', event => init(event.detail?.scope || document));
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root,state] of artistStates) if (event.target.contains(root)) {
      state.animations.forEach(animation => animation.cancel());
      artistStates.delete(root);
    }
    for (const [img,cleanup] of imageCleanups) if (event.target.contains(img)) cleanup();
    for (const node of event.target.querySelectorAll(cardSelector)) cardObserver?.unobserve(node);
  });
  reduced.addEventListener('change', () => {
    if (reduced.matches) for (const animation of running) animation.cancel();
  });
  window.addEventListener('pageshow', () => {
    for (const animation of running) animation.cancel();
    init(document, true);
  });
  window.IdolnestMotion = {init};
  init(document, true);
  const info = document.querySelector('[data-in-product] .in-product-info');
  if (info) animate(info,[{transform:'translate3d(0,8px,0)'},{transform:'translate3d(0,0,0)'}],{duration:360});
})();
